import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const videos = [
  '/videos/corporate-hustle.mp4',
  '/videos/earth-from-space.mp4'
];

const Hero = () => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [showContent, setShowContent] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Show content immediately
  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Initialize first video — skip during react-snap prerendering to avoid buffering requests
  useEffect(() => {
    const isHeadless = navigator.webdriver || /HeadlessChrome/.test(navigator.userAgent);
    if (isHeadless) return;

    const video = videoRef.current;
    if (!video) return;

    video.src = videos[0];
    video.load();

    // Try to play the video
    const playVideo = async () => {
      try {
        await video.play();
      } catch {
        // Autoplay prevented — user interaction required
      }
    };

    playVideo();
  }, []);

  // Handle video end - transition to next video
  useEffect(() => {
    const isHeadless = navigator.webdriver || /HeadlessChrome/.test(navigator.userAgent);
    if (isHeadless) return;

    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => {
      const nextIndex = (currentVideoIndex + 1) % videos.length;
      setCurrentVideoIndex(nextIndex);
      video.src = videos[nextIndex];
      video.load();
      video.play().catch(() => {});
    };

    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, [currentVideoIndex]);

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        {/* Video element */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute min-w-full min-h-full object-cover"
        >
          Your browser does not support the video tag.
        </video>
        
        {/* Overlay - Always dark in both modes */}
        <div className="absolute inset-0 bg-black/50 keep-dark" />
      </div>

      {/* Content - Always white text on dark background */}
      <div className="relative z-10 text-center keep-dark">
        <h1
          className={`text-5xl md:text-7xl font-bold text-white mb-8 leading-tight tracking-tight
            transition-all duration-1000 ease-out-expo fade-in-up
            ${showContent ? 'is-visible' : ''}`}
        >
          We Ship Production Grade AI Systems in{' '}
          <span className="line-through opacity-60">Months</span>{' '}
          <span className="text-teal-400">Weeks</span>
        </h1>
        <Link
          to="/solutions"
          className={`bg-teal-500 text-white px-8 py-4 rounded-md text-lg font-medium
            hover:bg-teal-400 transition-all duration-700 ease-out-expo
            flex items-center space-x-3 group shadow-lg inline-flex mx-auto btn-primary fade-in-up
            ${showContent ? 'is-visible' : ''}`}
          style={{
            transitionDelay: '200ms',
          }}
        >
          <span>Learn More</span>
          <ArrowRight className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
};

export default Hero;